import { useEffect, useState } from "react";
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  Button,
  FormHelperText,
  Checkbox,
  ListItemText,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import translations from "../../translations/updateUserFormTranslation";
import ClearIcon from "@mui/icons-material/Clear";
import { useAllBranches } from "../../contexts/BranchesContext";
import { isTestEnv } from "../../globals";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { useUser } from "../../contexts/UserContext";

const ACCENT = "#1976d2";

const UpdateUserForm = ({ rolesList, user, onUpdateUser, onCancel, isAdmin }) => {

  const [hospitals, setHospitals] = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const { user: myUser } = useUser();

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  //user.Role = id array
  //updatedUser.Role = json include id and names

  const [updatedUser, setUpdatedUser] = useState(user);
  const { allBranches } = useAllBranches();
  isTestEnv && console.log("rolesList", rolesList);

  // בדיקה האם למשתמש יש הרשאת ChemoRecordsPage
  const hasChemoRecordsPermission = () => {
    if (!updatedUser.Role || !rolesList.length) return false;

    const userRoleIds = Array.isArray(updatedUser.Role)
      ? updatedUser.Role
      : Object.keys(updatedUser.Role);

    return userRoleIds.some(roleId => {
      const role = rolesList.find(r => r._id === roleId);
      return role && role.ChemoRecordsPage === true;
    });
  };

  // שליפת רשימת בתי חולים מהבקאנד כשיש הרשאת ChemoRecords
  useEffect(() => {
    const fetchHospitals = async () => {
      if (hasChemoRecordsPermission() && hospitals.length === 0) {
        setLoadingHospitals(true);
        try {
          const response = await execute(
            "GET",
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/parameter12Values`
          );

          if (response && Array.isArray(response)) {
            setHospitals(response);
          }
        } catch (error) {
          console.error("Error fetching hospitals:", error);
          setHospitals([]);
        } finally {
          setLoadingHospitals(false);
        }
      }
    };

    fetchHospitals();
  }, [updatedUser.Role, execute]);


  useEffect(() => {
    isTestEnv && console.log("setUpdatedUser");
    if (rolesList.length > 0) {
      const jsonRoles = user.Role.reduce((acc, roleId) => {
        const role = rolesList.find((role) => role._id === roleId);
        if (role) {
          acc[roleId] = role.Name; // Assign role name to corresponding roleId
        }
        return acc;
      }, {});
      setUpdatedUser({ ...user, Role: jsonRoles });
    }
  }, [user, rolesList]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setUpdatedUser((prevUser) => ({ ...prevUser, [name]: value }));
  };
  const handleRoleChange = (event) => {

    const { value } = event.target;
    isTestEnv && console.log("handleRoleChange", value);
    if (rolesList.length > 0) {
      const jsonRoles = value.reduce((acc, roleName) => {
        const role = rolesList.find((role) => role.Name === roleName);
        if (role) {
          acc[role._id] = role.Name; // Assign role name to corresponding roleId
        }
        return acc;
      }, {});
      setUpdatedUser((prevUser) => ({ ...prevUser, Role: jsonRoles }));
    }
  };
  const handleBranchesChange = (event) => {
    isTestEnv && console.log("eventTarget", event.target);
    const { value } = event.target;

    if (Array.isArray(value) && value.includes("בחר הכל")) {
      setUpdatedUser((prevUser) => ({ ...prevUser, Branches: [] }));
    } else {
      setUpdatedUser((prevUser) => ({ ...prevUser, Branches: value }));
    }
  };

  const handleHospitalChange = (event) => {
    const { value } = event.target;
    setUpdatedUser((prevUser) => ({ ...prevUser, Hospital: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onUpdateUser(updatedUser);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={700} color={ACCENT} sx={{ mb: 0.5 }}>
            פרטים אישיים
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="Name"
            label={translations.firstName}
            value={updatedUser.Name || user.Name}
            onChange={handleInputChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="LastName"
            label={translations.lastName}
            value={updatedUser.LastName || user.LastName}
            onChange={handleInputChange}
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="Email"
            label={translations.email || "אימייל"}
            value={updatedUser.Email.toLowerCase() || user.Email.toLowerCase()}
            onChange={handleInputChange}
            fullWidth
            required
            type="email"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="PriorityUserName"
            label={translations.username || "שם משתמש בפריוריטי"}
            value={
              updatedUser.PriorityUserName?.toLowerCase() ||
              user.PriorityUserName?.toLowerCase()
            }
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={700} color={ACCENT} sx={{ mt: 1.5, mb: 0.5 }}>
            הרשאות
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Select
            name="Role"
            multiple
            value={updatedUser?.Role ? Object.values(updatedUser?.Role) : []}
            onChange={handleRoleChange}
            fullWidth
            required
          >
            {rolesList
              .filter((role) => {
                const isEditingAdmin = myUser?.roles?.some((r) => r.admin === true);

                if (isEditingAdmin) {
                  return true;
                }

                return myUser?.roles?.some((r) => r._id === role._id);
              })
              .map((role) => (
                <MenuItem key={role._id} value={role.Name}>
                  {role.Name}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>{translations.selectMultipleRoles}</FormHelperText>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Select
            name="Branches"
            multiple
            value={updatedUser.Branches || user.Branches || []}
            onChange={handleBranchesChange}
            fullWidth
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return <span style={{ color: '#999' }}>הרשאה לכל הסניפים</span>;
              }
              return selected.join(", ");
            }}
          >
            <MenuItem value="בחר הכל">
              <Checkbox
                checked={(updatedUser.Branches || user.Branches || []).length === 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setUpdatedUser(prev => ({ ...prev, Branches: [] }));
                  }
                }}
              />
              <ListItemText primary="בחר הכל" />
            </MenuItem>
            {Array.isArray(allBranches) && allBranches?.map((branch) => (
              <MenuItem key={branch.BRANCHNAME} value={branch.BRANCHNAME}>
                {branch.BRANCHNAME}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>* השארת שדה ריק = הרשאה לכל הסניפים</FormHelperText>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={700} color={ACCENT} sx={{ mt: 1.5, mb: 0.5 }}>
            הגדרות נוספות
          </Typography>
        </Grid>

        <Grid item xs={12} sm={hasChemoRecordsPermission() ? 6 : 12}>
          <Select
            name="tradeDelayRole"
            value={updatedUser.tradeDelayRole || 'none'}
            onChange={(e) =>
              setUpdatedUser((prev) => ({
                ...prev,
                tradeDelayRole: e.target.value === 'none' ? null : e.target.value,
              }))
            }
            fullWidth
            disabled={!isAdmin}
          >
            <MenuItem value="none">—</MenuItem>
            <MenuItem value="headManager">מנהל ראשי</MenuItem>
            <MenuItem value="manager">מנהל</MenuItem>
            <MenuItem value="employee">מתאמת סחר</MenuItem>
            <MenuItem value="supplier">ספק חיצוני</MenuItem>
          </Select>
          <FormHelperText>{translations.tradeDelay || "השהיית סחר"}</FormHelperText>
        </Grid>

        {/* שדה בית חולים - מופיע רק למשתמשים עם הרשאת ChemoRecordsPage */}
        {hasChemoRecordsPermission() && (
          <Grid item xs={12} sm={6}>
            <Select
              name="Hospital"
              value={updatedUser.Hospital || user.Hospital || ""}
              onChange={handleHospitalChange}
              fullWidth
              displayEmpty
              disabled={loadingHospitals}
            >
              <MenuItem value="">
                <em>{loadingHospitals ? "טוען..." : "לא נבחר"}</em>
              </MenuItem>
              {loadingHospitals ? (
                <MenuItem disabled>
                  <em>טוען בתי חולים...</em>
                </MenuItem>
              ) : (
                hospitals.map((hospital) => (
                  <MenuItem key={hospital.SPECVALUE} value={hospital.SPECVALUE}>
                    {hospital.SPECVALUE}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>בית חולים</FormHelperText>
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={onCancel}
          sx={{ borderRadius: 2.5, textTransform: "none" }}
        >
          {translations.cancelChanges}
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<CheckIcon />}
          sx={{
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 600,
            background: ACCENT,
          }}
        >
          {translations.save}
        </Button>
      </Box>
    </Box>
  );
};

export default UpdateUserForm;
